/* eslint-disable comma-dangle */
import React, { ReactElement, useState, useEffect } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { initialAppState, updateAppState } from 'appState/AppState';
import { Footer } from '@novozymes-digital/components';
import Result from 'pages/Result';
import Header from 'layout/Header';
import { AppState, InputData, PriceInput } from 'appState/AppStateTypes';
import getEuroToUsd from 'services/CurrencyService';
import Converter from 'utils/Converter';
import IEUnsupportedDialog from 'components/dialogs/IEUnsupportedDialog';
import useIsDesktop from 'utils/MediaQueryHelper';

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#f6f7f5',
    minHeight: '100%',
    [theme.breakpoints.up(1280)]: {
      overflowX: 'hidden',
    },
  },
}));

function App(): ReactElement {
  const [appState, setAppState] = useState(initialAppState);
  const [converter, setConverter] = useState<Converter | undefined>(undefined);

  const classes = useStyles();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    getEuroToUsd().then((euroToUsd) => setConverter(new Converter(euroToUsd)));
  }, []);

  const handleInputChange =
    (attr: string) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const appStateClone: AppState = { ...appState };
      const value = parseFloat(event.target.value);
      appStateClone.inputFields = Object.fromEntries(
        Object.entries(appStateClone.inputFields).map(([k, field]) => [k, k === attr ? { ...field, value } : field])
      ) as InputData;

      appStateClone.priceInputFields = Object.fromEntries(
        Object.entries(appStateClone.priceInputFields).map(([k, field]) => [
          k,
          k === attr ? { ...field, value } : field,
        ])
      ) as PriceInput;

      if (converter) {
        setAppState(updateAppState(appStateClone, appState, converter));
      }
    };

  const updateSeveralValues = (newValues: { attr: string; value: number }[]) => {
    const appStateClone: AppState = { ...appState };
    newValues.forEach(({ attr, value }) => {
      appStateClone.inputFields = Object.fromEntries(
        Object.entries(appStateClone.inputFields).map(([k, field]) => [k, k === attr ? { ...field, value } : field])
      ) as InputData;

      appStateClone.priceInputFields = Object.fromEntries(
        Object.entries(appStateClone.priceInputFields).map(([k, field]) => [
          k,
          k === attr ? { ...field, value } : field,
        ])
      ) as PriceInput;
    });

    if (converter) {
      setAppState(updateAppState(appStateClone, appState, converter));
    }
  };

  const handleSelectChange =
    (attr: string) =>
    (event: React.ChangeEvent<{ value: unknown }>): void => {
      const appStateClone: AppState = { ...appState };
      appStateClone.inputFields = Object.fromEntries(
        Object.entries(appStateClone.inputFields).map(([k, field]) => [
          k,
          k === attr ? { ...field, value: event.target.value as number } : field,
        ])
      ) as InputData;
      if (converter) {
        setAppState(updateAppState(appStateClone, appState, converter));
      }
    };
  useEffect(() => {
    if (document.getElementById('gtm-init')) return;
    const script = document.createElement('script');
    script.id = 'gtm-init';
    script.innerHTML = `(function(w,d,s,l,g,i,h){w[l]=w[l]||[];w[l].push({gcm:1,
      server:h});w[g]=function(){w[l].push(arguments)};var c='denied';
      w[g]('consent','default',{ad_storage:c,ad_user_data:c,
      ad_personalization:c,analytics_storage:c,functionality_storage:c,
      personalization_storage:c,security_storage:'granted',
      wait_for_update:500});w[g]('set','ads_data_redaction',true);
      w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],j=d.createElement(s),
      dl=l!='dataLayer'?'&l='+l:'';j.async=true;
      j.src='https://'+(h??'www.googletagmanager.com')+'/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);})(window,document,'script',
      'dataLayer','gtag','GTM-KTWCZXC','gtm.novozymes.cloud');`;
    document.head.appendChild(script);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      script.parentNode?.removeChild(script);
    };
  }, []);

  // OneTrust consent cookie script
  useEffect(() => {
    if (document.getElementById('onetrust-sdk')) return;
    const cookieScript = document.createElement('script');
    cookieScript.id = 'onetrust-sdk';
    cookieScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    cookieScript.type = 'text/javascript';
    cookieScript.setAttribute('data-document-language', 'true');
    cookieScript.setAttribute('data-domain-script', '019d2144-2295-7409-b72b-d200bfc4ee59');
    document.head.prepend(cookieScript);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      cookieScript.parentNode?.removeChild(cookieScript);
    };
  }, []);

  return (
    <Box className={classes.container}>
      <IEUnsupportedDialog />
      <Router>
        <Header appState={appState} />
        <Switch>
          <Route exact path="/">
            <Result
              handleInputChange={handleInputChange}
              handleSelectChange={handleSelectChange}
              updateSeveralValues={updateSeveralValues}
              appState={appState}
            />
          </Route>
        </Switch>
        <Box style={{ paddingLeft: isDesktop ? undefined : '0.5rem' }}>
          <Footer />
        </Box>
      </Router>
    </Box>
  );
}

export default App;
